import React, { Fragment, Component } from 'react';
import { camelizeKeys } from 'humps';
import { Table, Collapse, Button } from 'reactstrap';

import api from '../../lib/api';
import endpoints from '../../lib/endpoints';
import Toggler from '../../components/UI/Toggler';
import makeUrl from '../../lib/makeUrl';
import { truncateText } from '../../lib/utils';
import SortAndFilter from './SortAndFilter';
import {
  getHoursSinceBooking,
  formatDateWithDays,
  getDaysBetween,
  formatDate,
} from './utils';

type Status = {
  type: string,
  ts: string,
};

type Money = {
  amount: number,
  currency: string,
};

type Lodge = {
  id: string,
  email: string,
  name: string,
  owner: string,
  phoneNumbers: number,
  address: string,
};

type Trekker = {
  email: string,
  id: string,
  name: string,
};

type Props = {
  bookingId: string,
  checkinDate: string,
  checkoutDate: string,
  lodge: Lodge,
  pax: number,
  paymentId: string,
  bookingDate: string,
  status: Array<Status>,
  trekId: string,
  trekker: Trekker,
  totalAmount: Money,
  payableAmount: Money,
  paymentPlan: string,
  trekId: null | string,
  roomType: string,
  companyName: null | string,
  groupLeader: null | string,
  groupName: null | string,
};

type Data = {
  data: Props,
};

const TableHeaders = [
  'Booking Date',
  'Time since(hrs)',
  'Trekker Name',
  'Check In',
  '',
  'PAX',
  'PP',
  'Status',
  '',
];

const LodgeBookings = (props: Data) => (
  <SortAndFilter>
    {({ sortBookings, showOrHide }) => (
      <div className="col-12">
        <Table responsive className="admin-table">
          <thead>
            <tr>
              {TableHeaders.map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortBookings(props.data).map(d => {
              const { bookingId } = d;
              return (
                <Toggler key={bookingId}>
                  {({ on, toggle }) => (
                    <LodgeBookingRow
                      data={d}
                      on={on}
                      toggle={toggle}
                      showOrHide={showOrHide}
                    />
                  )}
                </Toggler>
              );
            })}
          </tbody>
        </Table>
      </div>
    )}
  </SortAndFilter>
);

class LodgeBookingRow extends Component {
  state = { lodge: null };

  toggleWithApi = async () => {
    const { lodge } = this.state;
    const { toggle } = this.props;

    if (lodge) {
      toggle();
      return;
    }

    const { data } = await api.get(endpoints.lodge.details(this.props.data.itemData.id));
    this.setState({ lodge: camelizeKeys(data) }, () => toggle());
  };

  render() {
    const { lodge } = this.state;
    const { data, on, showOrHide } = this.props;
    const { itemData, status, trekker } = data;
    const {
      bookingDate,
      checkinDate,
      checkoutDate,
      roomType,
      pax,
      totalAmount,
      paymentPlan,
      payableAmount,
      remainingAmount: dueAmount,
      companyName,
      groupLeader,
      groupName,
    } = itemData;
    const daysBetweenBookingAndCheckin = getDaysBetween(bookingDate, checkinDate);

    const statusType = chooseType(getStatus(status));
    const statusColor = chooseColour(getStatus(status));

    console.log(statusType);

    return (
      <Fragment>
        <tr
          style={{
            ...showOrHide(statusType),
            backgroundColor: statusColor,
          }}
        >
          <td>{formatDate(bookingDate)}</td>

          <td>{getHoursSinceBooking(bookingDate)}</td>

          <td>
            {trekker && (
              <a
                href={trekker.fullname && makeUrl.goToProfile(trekker.fullname, trekker.id)}
                title={trekker.fullname}
              >
                {trekker.fullname && truncateText(trekker.fullname, 12)}
              </a>
            )}
          </td>

          <td>{formatDateWithDays(checkinDate)}</td>
          <td>{daysBetweenBookingAndCheckin} days</td>

          <td>{pax}</td>
          <td>{paymentPlan}</td>
          <td>{statusType}</td>
          <td>
            <Button onClick={this.toggleWithApi}>+</Button>
          </td>
        </tr>
        <tr style={showOrHide(statusType)}>
          <td colSpan="10">
            <Collapse isOpen={on} className="more-detail">
              {lodge && (
                <ul>
                  <li className="title">Lodge Details</li>
                  <li>
                    Name:
                    <a
                      href={makeUrl.goToLodgeFromPlace(lodge.name, lodge.id, lodge.address)}
                      title={`${lodge.name}, ${lodge.address}`}
                    >
                      {' '}
                      {`${truncateText(lodge.name, 8)},${truncateText(lodge.address, 6)}`}
                    </a>
                  </li>

                  <li>Email: {lodge.email || ''}</li>
                  <li>
                    Phone:{(lodge.phoneNumbers && lodge.phoneNumbers.join(', ')) || ''}
                  </li>
                  <li>Owner: {lodge.ownerName || ''}</li>
                  <li>Id: {lodge.id}</li>
                </ul>
              )}

              {trekker && (
                <ul>
                  <li className="title">Trekker Details</li>
                  <li>Email: {trekker.email || ''}</li>
                  <li>Id: {trekker.id}</li>
                </ul>
              )}

              <ul>
                <li className="title">Payment Details</li>
                <li>Payable Amount: {payableAmount}</li>
                <li>Total: {totalAmount}</li>
                <li>Due: {!isNaN(dueAmount) && dueAmount}</li>
              </ul>

              <ul>
                <li className="title">Additional Details</li>
                <li>Check-Out Date: {formatDateWithDays(checkoutDate) || ''}</li>
                {/* <li>Trek Id: {trekId || ''}</li> */}
                <li>Room Type: {roomType || ''}</li>
                <li>Group Name: {groupName || ''}</li>
                <li>Group Leader: {groupLeader || ''}</li>
                <li>Company Name: {companyName || ''}</li>
              </ul>
            </Collapse>
          </td>
        </tr>
      </Fragment>
    );
  }
}

const findStatus = (statusArr, type) => statusArr.find(status => status.type === type);

const getStatus = statusArr => {
  const acceptedStatus = findStatus(statusArr, 'Accepted');
  const rejectedStatus = findStatus(statusArr, 'Rejected');
  const pendingStatus = findStatus(statusArr, 'Pending');

  if (acceptedStatus) return acceptedStatus;
  if (rejectedStatus) return rejectedStatus;
  if (pendingStatus) return pendingStatus;
};

const chooseType = status => status.type;

const chooseColour = status => {
  const { ts, type } = status;

  if (type === 'Accepted') return 'rgba(0, 128, 0, 0.2)';
  if (type === 'Rejected') return 'rgba(255, 0, 0, 0.2)';
  if (type === 'Pending') {
    const hrs = getHoursSinceBooking(ts);

    if (hrs <= 12) return 'white';
    if (hrs > 12 && hrs < 24) return 'rgba(255, 255, 0, 0.2)';
    if (hrs >= 24) return 'rgba(255, 165, 0, 0.2)';
  }
};

export default LodgeBookings;



// WEBPACK FOOTER //
// ./src/screens/BookingsDashboard/LodgeBookings.jsx